import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { applyPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { locationsGetById, locationsUpdate } from "~/api-refs";
import {
  type Location,
  LocationAddressField,
  LocationBusinessHoursField,
  LocationLogoField,
  LocationNameField,
  LocationPhoneNumberField,
  LocationStyleAndTypeField,
} from "~/components/locations/location-form-fields";
import { LoadingPage } from "~/components/shared/loading-page";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute("/_authenticated/locations/$id/edit")({
  component: EditLocationModal,
});

type Step1Data = Pick<Location, "logoUrl" | "name" | "type" | "style">;
type Step2Data = Pick<Location, "address" | "phoneNumber">;
type Step3Data = Pick<
  Location,
  "businessHours" | "operationalStatus" | "closeDateRange"
>;

function EditLocationModal() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1Data | undefined>();
  const [step2Data, setStep2Data] = useState<Step2Data | undefined>();
  const [step3Data, setStep3Data] = useState<Step3Data | undefined>();
  const [initialized, setInitialized] = useState(false);

  const { data: location, isLoading } = useQuery(
    convexQuery(locationsGetById, {
      id: toConvexTableId("locations", id),
    })
  );

  // Initialize form data from fetched location
  useEffect(() => {
    if (!location || initialized) {
      return;
    }

    setStep1Data({
      logoUrl: location.logoUrl ?? null,
      name: location.name,
      type: location.type,
      style: location.style,
    });
    setStep2Data({
      address: location.address
        ? {
            addressLine1: location.address.addressLine1,
            addressLine2: location.address.addressLine2 ?? "",
            city: location.address.city,
            state: location.address.state,
            zipCode: location.address.zipCode,
          }
        : {
            addressLine1: "",
            addressLine2: "",
            city: "",
            state: "",
            zipCode: "",
          },
      phoneNumber: location.phoneNumber ?? "",
    });
    setStep3Data({
      businessHours: location.businessHours.map(
        ({ dayOfTheWeek, openTime, closeTime }) => ({
          dayOfTheWeek,
          openTime: openTime ?? null,
          closeTime: closeTime ?? null,
        })
      ),
      operationalStatus: location.operationalStatus ?? "OPEN",
      closeDateRange: {
        closeStartDate: null,
        closeEndDate: null,
      },
    });
    setInitialized(true);
  }, [location, initialized]);

  const handleClose = () => {
    void navigate({ to: `/locations/${id}/profile` });
  };

  const updateLocationMutation = useMutation({
    mutationFn: (params: {
      id: Id<"locations">;
      data: Step1Data & Step2Data & Step3Data;
    }) => {
      return convexClient.mutation(locationsUpdate, params);
    },
  });

  const updateLocation = async (data: Step1Data & Step2Data & Step3Data) => {
    try {
      const result = await updateLocationMutation.mutateAsync({
        id: toConvexTableId("locations", id),
        data,
      });
      if (!result.success) {
        logger.error("location_update_failed", { error: result.error });
        return;
      }
      // Track location update in PostHog.
      ProductEvents.locationUpdated(id, "full_edit");
      handleClose();
    } catch (error) {
      logger.exception("location_update_exception", error);
    }
  };

  const handleStep1Submit = (data: Step1Data) => {
    setStep1Data(data);
    setCurrentStep(1);
  };

  const handleStep2Submit = (data: Step2Data) => {
    setStep2Data(data);
    setCurrentStep(2);
  };

  const handleStep3Submit = async (data: Step3Data) => {
    if (!(step1Data && step2Data)) {
      return;
    }
    await updateLocation({
      ...step1Data,
      ...step2Data,
      ...data,
    });
  };

  const stepTitles = ["Edit location", "Contact details", "Business hours"];
  const stepDescriptions = [
    "Update the details of your location below",
    "Update your location contact details below",
    "Update your location's usual business hours or closures",
  ];

  if (isLoading || !initialized) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <DialogContent noCloseButton noScrollPrevention>
          <LoadingPage />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noCloseButton noScrollPrevention>
        <div className="space-y-5">
          {/* Step indicator */}
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((step) => (
              <button
                className={twJoin(
                  "h-1.5 rounded-full",
                  step <= currentStep ? "bg-primary" : "bg-muted"
                )}
                key={step}
                onClick={
                  step < currentStep ? () => setCurrentStep(step) : undefined
                }
                type="button"
              />
            ))}
          </div>

          <DialogHeader className="space-y-0">
            <DialogTitle className="subtitle font-heading text-foreground text-center">
              {stepTitles[currentStep]}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-center">
              {stepDescriptions[currentStep]}
            </DialogDescription>
          </DialogHeader>

          {currentStep === 0 && step1Data && (
            <LocationFormStep1 data={step1Data} onSubmit={handleStep1Submit} />
          )}
          {currentStep === 1 && step2Data && (
            <LocationFormStep2 data={step2Data} onSubmit={handleStep2Submit} />
          )}
          {currentStep === 2 && step3Data && (
            <LocationFormStep3
              data={step3Data}
              isUpdating={updateLocationMutation.isPending}
              onSubmit={handleStep3Submit}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LocationFormStep1({
  onSubmit,
  data,
}: {
  onSubmit: (data: Step1Data) => void;
  data: Step1Data;
}) {
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<Step1Data>({
    defaultValues: data,
  });

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <LocationLogoField
          control={form.control}
          name="logoUrl"
          setUploading={setIsUploading}
        />
        <LocationNameField control={form.control} name="name" />
        <LocationStyleAndTypeField
          control={form.control}
          resetField={form.resetField}
          styleField="style"
          typeField="type"
          watch={form.watch}
        />
        <Button className="w-full" disabled={isUploading} size="lg">
          {isUploading ? "Uploading image..." : "Next"}
        </Button>
      </form>
    </Form>
  );
}

function LocationFormStep2({
  onSubmit,
  data,
}: {
  onSubmit: (data: Step2Data) => void;
  data: Step2Data;
}) {
  const form = useForm<Step2Data>({
    defaultValues: {
      address: data.address,
      phoneNumber: applyPhoneNumberMask(data.phoneNumber),
    },
  });

  return (
    <Form {...form}>
      <form
        className="space-y-5"
        onSubmit={form.handleSubmit((formData) => {
          const cleanedData = {
            ...formData,
            phoneNumber: formData.phoneNumber
              ? `+1${formData.phoneNumber.replace(/\D/g, "")}`
              : "",
          };
          onSubmit(cleanedData);
        })}
      >
        <LocationAddressField
          addressLine1Field="address.addressLine1"
          addressLine2Field="address.addressLine2"
          cityField="address.city"
          control={form.control}
          resetField={form.resetField}
          stateField="address.state"
          zipCodeField="address.zipCode"
        />
        <LocationPhoneNumberField control={form.control} name="phoneNumber" />
        <Button className="w-full" size="lg">
          Next
        </Button>
      </form>
    </Form>
  );
}

function LocationFormStep3({
  onSubmit,
  data,
  isUpdating,
}: {
  onSubmit: (data: Step3Data) => void;
  data: Step3Data;
  isUpdating: boolean;
}) {
  const form = useForm<Step3Data>({
    defaultValues: data,
  });

  return (
    <Form {...form}>
      <form
        className="mx-auto flex flex-col items-center gap-5"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <LocationBusinessHoursField
          closeEndDateField="closeDateRange.closeEndDate"
          closeStartDateField="closeDateRange.closeStartDate"
          control={form.control}
          operationalStatusField="operationalStatus"
          resetField={form.resetField}
          watch={form.watch}
        />
        <Button className="w-full" disabled={isUpdating} size="lg">
          {isUpdating ? "Saving..." : "Save Changes"}
        </Button>
      </form>
    </Form>
  );
}
